'use client'

import React from 'react';
import { Menu, Transition } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';

// ========================================================================
// --- Type Definitions (Mirroring the Parent) ---
// ========================================================================

// These types are identical to the ones in the parent ListWithIcons.tsx.
// It's a good practice to move these to a shared types file to avoid duplication.

interface ItemWithId {
  id: string | number;
  [key: string]: any;
}

interface ItemOption<T> {
  name: string;
  onClick: (item: T) => void;
}

// --- The Props for THIS component ---
interface Props<T extends ItemWithId> {
  item: T;
  titleKey: keyof T;
  onItemClick: (item: T) => void;
  itemOptions?: ItemOption<T>[];
  subtitleKey?: keyof T;
  subtitleName?: string;
}

// A simple helper for class names
function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}


// ========================================================================
// --- The Component ---
// ========================================================================

const ListWithIconsElement = <T extends ItemWithId>({
  item,
  titleKey,
  onItemClick,
  itemOptions,
  subtitleKey,
  subtitleName,
}: Props<T>) => {
  // --- Safely access data using the provided keys ---
  // Using `item[key]` allows us to dynamically access properties.
  const title = item[titleKey] || '';
  const subtitle = subtitleKey ? item[subtitleKey] : null;
  // console.log('item: ', item)

  return (
    <div className="flex items-center justify-between gap-x-6 py-3 p-6">
      {/* Main content area */}
      <div className="flex min-w-0 items-start gap-x-4">
        {/* Placeholder for an icon/avatar if you have one */}
        {item.originalPerson?.icon ? <img className="h-12 w-12 flex-none rounded-full bg-gray-50" src={item.originalPerson.icon} alt="" />:null}
        {item?.icon ? <img className="h-12 w-12 flex-none rounded-full bg-gray-50" src={item.icon} alt="" />:null}
        
        <div className="min-w-0 flex-auto">
          <button
            type="button"
            onClick={() => onItemClick(item)}
            className="text-left text-sm font-semibold leading-6 text-gray-900 hover:text-blue-600 transition-colors"
          >
            {title}
          </button>
          
          {subtitle && subtitleKey && (
            <p className="mt-1 flex text-xs leading-5 text-gray-500">
              {subtitleName && <span className="font-medium mr-1">{subtitleName}:</span>}
              <span>{subtitle}</span>
            </p>
          )}
        </div>
      </div>

      {/* Dropdown menu for options */}
      {itemOptions && itemOptions.length > 0 && (
        <Menu as="div" className="relative flex-none">
          <Menu.Button className="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900">
            <span className="sr-only">Open options</span>
            <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
          </Menu.Button>
          <Transition
            as={React.Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
              {itemOptions.map((option) => (
                <Menu.Item key={option.name}>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={() => option.onClick(item)}
                      className={classNames(
                        active ? 'bg-gray-50' : '',
                        'block w-full text-left px-3 py-1 text-sm leading-6 text-gray-900'
                      )}
                    >
                      {option.name}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </Menu>
      )}
    </div>
  );
};

export default ListWithIconsElement;